import { CommonModule, Location } from '@angular/common';
import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { IonicModule, ModalController, NavController, AlertController } from '@ionic/angular';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { ImageModalComponent } from 'src/app/components/image-modal/image-modal.component';
import { PROJECT_MEMBER_ROLES } from 'src/app/constants';
import { fileNameFromImageModalUpload } from 'src/app/utils/utils';
import { Tenant, Upload } from 'src/models';
import { Portal, PortalFormModel } from 'src/models/portals';
import { Project, ProjectMember } from 'src/models/projects';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { PortalsService } from 'src/services/portals.service';
import { ProjectsService } from 'src/services/projects.service';
import { TenantsService } from 'src/services/tenants.service';
import { UploadService } from 'src/services/upload.service';

export interface PortalConfig {
  isExternal: boolean;
  domainSuffix: string;
  title: string;
  noPortalMessage: string;
  noPortalDescription: string;
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, SharedPipesModule, NgxTippyModule],
  selector: 'app-portals-base',
  templateUrl: './portals-base.component.html',
  styleUrls: ['./portals-base.component.scss'],
})
export class PortalsBaseComponent implements OnInit, OnDestroy {
  @Input() config!: PortalConfig;

  portals: Partial<Portal>[] = [];
  projects: Partial<Project>[] = [];
  hasPortal: boolean = false;
  isCreatingPortal: boolean = false;
  isEditingPortal: boolean = false;
  editingPortalId: string | null = null;
  tenant: Tenant | null = null;

  // Tooltip content
  tooltips = {
    customDomain: `The domain where your portal will be accessible (e.g., support.${this.tenant?.subdomain || 'yourcompany'}.com). When disabled, a preview URL will be available for sharing with colleagues.`,
    logo: 'Your company logo that will be displayed on the portal',
    status:
      'Live portals are accessible to customers or employees; draft portals can only be seen by admins and can be shared via preview links',
    primaryColor: 'The main color theme used throughout the portal interface',
    projects:
      'Knowledge bases and other resources associated with these projects will be accessible via this customer portal.',
  };

  portalForm: PortalFormModel = {
    customDomain: '',
    primaryColor: '#000000',
    selectedProjectIds: [],
    supportLogo: null,
    isEnabled: false,
  };

  isLoading = false;
  isInitialLoading = true; // Track initial loading state
  isUploadingLogo = false; // Track logo upload loading state
  isDnsTesting = false; // Track DNS test loading state
  isTlsTesting = false; // Track TLS test loading state
  dnsTestSteps = [
    'Resolving domain...',
    'Checking CNAME records...',
    'Verifying DNS configuration...',
    'Testing connection...',
  ];
  tlsTestSteps = ['Checking certificate status...', 'Verifying SSL configuration...', 'Validating certificate...'];
  currentDnsStep = 0;
  currentTlsStep = 0;
  currentTestingPortal: Portal | null = null;

  routerSub: Subscription;

  constructor(
    public authService: AuthService,
    private modalCtrl: ModalController,
    private navCtrl: NavController,
    private portalsService: PortalsService,
    private msgSrvc: MessageService,
    private projectsService: ProjectsService,
    private tenantsService: TenantsService,
    private uploadService: UploadService,
    private apiService: ApiService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private alertCtrl: AlertController,
  ) {}

  async ngOnInit() {
    await Promise.all([this.getProjects(), this.getTenant(), this.getPortals()]);
    const currentPath = this.location.path();

    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd && currentPath === e.url))
      .subscribe(async () => {
        this.isLoading = true;

        await Promise.all([this.getPortals()]);

        this.isLoading = false;
      });
  }

  ngOnDestroy() {
    this.routerSub.unsubscribe();
  }

  async getTenant() {
    try {
      this.tenant = await this.tenantsService.getTenant();
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  async getPortals() {
    try {
      if (this.authService.isTenantAdmin) {
        const portalsResponse = await this.portalsService.list({
          limit: null,
          descending: false,
          where: [
            {
              field: 'isExternal',
              operator: '==',
              value: this.config.isExternal,
            },
          ],
        });

        this.portals = portalsResponse.data;
        this.hasPortal = portalsResponse.data.length > 0;
      }
    } catch (err) {
      this.msgSrvc.showError(err);
    } finally {
      this.isInitialLoading = false;
    }
  }

  async getProjects() {
    try {
      if (this.authService.isTenantAdmin) {
        const projectsResponse = await this.projectsService.list({
          where: [
            {
              field: 'tenantId',
              operator: '==',
              value: this.authService.tenantId,
            },
            // Filter projects based on portal type
            {
              field: 'isExternal',
              operator: '==',
              value: this.config.isExternal,
            },
            // Filter out deleted projects
            {
              field: 'deletedAt',
              operator: '==',
              value: null,
            },
          ],
        });

        this.projects = projectsResponse.data;
      } else {
        const projectsResponse = await this.projectsService.listForUser(this.authService.userId, {
          limit: null,
          order: ['project.name'],
          descending: false,
          where: [
            {
              field: 'role',
              operator: '==',
              value: PROJECT_MEMBER_ROLES.OWNER,
            },
            // Filter projects based on portal type
            {
              field: 'project.isExternal',
              operator: '==',
              value: this.config.isExternal,
            },
            // Filter out deleted projects
            {
              field: 'project.deletedAt',
              operator: '==',
              value: null,
            },
          ],
        });

        this.projects = projectsResponse.data.map(
          (item: Partial<ProjectMember> & { project?: Project }) => item.project!,
        );
      }
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  startCreatePortal() {
    this.isCreatingPortal = true;
    this.isEditingPortal = false;
    this.editingPortalId = null;
    this.portalForm = {
      customDomain: this.tenant?.subdomain ? `${this.tenant.subdomain}${this.config.domainSuffix}` : '',
      primaryColor: this.tenant?.primaryColor || '#000000',
      selectedProjectIds: this.projects.map((project) => project.id!),
      supportLogo: this.tenant?.logo || null,
      isEnabled: false,
    };
  }

  editPortal(portal: Portal) {
    this.isCreatingPortal = false;
    this.isEditingPortal = true;
    this.editingPortalId = portal.id;
    this.portalForm = {
      customDomain: portal.customDomain || '',
      primaryColor: portal.settings?.primaryColor || '#000000',
      selectedProjectIds: portal.projectIds || [],
      supportLogo: portal.settings?.logoUrl || null,
      isEnabled: portal.isEnabled,
    };
  }

  cancelPortalForm() {
    this.isCreatingPortal = false;
    this.isEditingPortal = false;
    this.editingPortalId = null;
    this.portalForm = {
      customDomain: '',
      primaryColor: '#000000',
      selectedProjectIds: [],
      supportLogo: null,
      isEnabled: false,
    };
  }

  async submitPortalForm() {
    // Validate domain
    const domainError = this.validateDomain(this.portalForm.customDomain);
    if (domainError) {
      this.msgSrvc.showError(domainError);
      return;
    }
    // Validate logo is required
    if (!this.portalForm.supportLogo) {
      this.msgSrvc.showError('Please upload a logo for your portal');
      return;
    }
    if (!this.portalForm.selectedProjectIds.length) {
      this.msgSrvc.showError('Please select at least one project');
      return;
    }

    this.isLoading = true;
    try {
      const portalData = {
        customDomain: this.portalForm.customDomain,
        isExternal: this.config.isExternal,
        isEnabled: this.portalForm.isEnabled,
        projectIds: this.portalForm.selectedProjectIds,
        settings: {
          logoUrl: this.portalForm.supportLogo || '',
          primaryColor: this.portalForm.primaryColor,
        },
      };

      if (this.isEditingPortal && this.editingPortalId) {
        await this.portalsService.update(this.editingPortalId, portalData);
        // Update local array
        const idx = this.portals.findIndex((p) => p.id === this.editingPortalId);
        if (idx !== -1) this.portals[idx] = { ...this.portals[idx], ...portalData };
        this.msgSrvc.show('Portal updated successfully');
      } else {
        const newPortal = await this.portalsService.create(portalData);
        this.portals.push(newPortal);
        this.hasPortal = true;
        this.msgSrvc.show('Portal created successfully');
      }
      this.cancelPortalForm();
    } catch (err) {
      this.msgSrvc.showError(err);
    } finally {
      this.isLoading = false;
    }
  }

  // Shared logo upload logic
  async uploadPortalLogo() {
    try {
      const modal = await this.modalCtrl.create({
        component: ImageModalComponent,
        componentProps: {
          title: 'Upload Logo',
          description: 'Upload a logo for your portal',
          aspectRatio: 1,
          maxWidth: 512,
          maxHeight: 512,
        },
      });
      await modal.present();
      const { data } = await modal.onWillDismiss();
      if (data && data.uploadFile) {
        this.isUploadingLogo = true;
        try {
          const uploadResult = await this.uploadService.upload(
            [data.uploadFile],
            'portal-logos',
            fileNameFromImageModalUpload(data.uploadFile),
          );
          this.portalForm.supportLogo = uploadResult[0].url;
        } catch (uploadError) {
          throw uploadError;
        } finally {
          this.isUploadingLogo = false;
        }
      }
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  getProjectName(projectId: string): string {
    const project = this.projects.find((p) => p.id === projectId);
    return project?.name || 'Unknown Project';
  }

  // Check if there are no projects for the current portal type
  get hasNoProjectsForPortalType(): boolean {
    return this.projects.length === 0;
  }

  // Get the project type name for the current portal
  get projectTypeName(): string {
    return this.config.isExternal ? 'external' : 'internal';
  }

  // Navigate to project creation
  navigateToProjectCreation() {
    this.navCtrl.navigateRoot('/dashboard/settings/project-list');
  }

  async viewPortal(id: string) {
    const portal = this.portals.find((p) => p.id === id);
    if (portal?.isEnabled && portal?.customDomain) {
      // Live portal - use custom domain
      window.open(`https://${portal.customDomain}`, '_blank');
    } else if (portal?.previewUrl) {
      // Preview portal - use preview URL
      window.open(portal.previewUrl, '_blank');
    }
  }

  // Generate preview URL for disabled portals
  generatePreviewUrl(portal: Portal): string {
    if (portal.isEnabled) {
      return `https://${portal.customDomain}`;
    }

    // Use stored preview URL if available, otherwise generate default
    if (portal.previewUrl) {
      return portal.previewUrl;
    }

    // For disabled portals, generate a default preview URL
    const baseUrl = window.location.origin;
    const previewPath = `/preview/portal/${portal.id}`;
    return `${baseUrl}${previewPath}`;
  }

  // Copy preview URL to clipboard
  async copyPreviewUrl(portal: Portal) {
    const previewUrl = this.generatePreviewUrl(portal);
    try {
      await navigator.clipboard.writeText(previewUrl);
      this.msgSrvc.show('Preview URL copied to clipboard');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = previewUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      this.msgSrvc.show('Preview URL copied to clipboard');
    }
  }

  // Test DNS connection for custom domain
  async testDnsConnection(portal: Portal) {
    if (!portal.customDomain) {
      this.msgSrvc.show('No custom domain configured');
      return;
    }

    this.isDnsTesting = true;
    this.currentTestingPortal = portal;
    this.currentDnsStep = 0;

    try {
      // Simulate DNS test steps with delays
      for (let i = 0; i < this.dnsTestSteps.length; i++) {
        this.currentDnsStep = i;
        await new Promise((resolve) => setTimeout(resolve, 800));
      }

      const result = await this.apiService.postPromise<{
        success: boolean;
        message?: string;
        reason?: string;
        cnameRecords?: string[];
        expectedTarget?: string;
        error?: string;
      }>('/portals/test-dns', {
        customDomain: portal.customDomain,
        portalId: portal.id,
      });

      if (result.success) {
        this.msgSrvc.show('✅ DNS connection successful! Your custom domain is properly configured.');
      } else {
        this.msgSrvc.show(result.message || '❌ DNS connection failed. Please check your CNAME record configuration.', {
          duration: 0,
          buttons: [
            {
              text: '✕',
              role: 'cancel',
              handler: () => {
                // Toast will be dismissed automatically
              },
            },
          ],
        });
      }
    } catch (error: any) {
      if (error.status === 403) {
        this.msgSrvc.show('⚠️ Access denied. Please try refreshing the page and try again.');
      } else {
        this.msgSrvc.show('⚠️ Connection test failed. Please verify your DNS settings manually.');
      }
    } finally {
      this.isDnsTesting = false;
      this.currentTestingPortal = null;
      this.currentDnsStep = 0;
    }
  }

  // Test TLS certificate for custom domain
  async testTlsCertificate(portal: Portal) {
    if (!portal.customDomain) {
      this.msgSrvc.show('No custom domain configured');
      return;
    }

    this.isTlsTesting = true;
    this.currentTestingPortal = portal;
    this.currentTlsStep = 0;

    try {
      // Simulate TLS test steps with delays
      for (let i = 0; i < this.tlsTestSteps.length; i++) {
        this.currentTlsStep = i;
        await new Promise((resolve) => setTimeout(resolve, 600));
      }

      const result = await this.apiService.postPromise<{
        success: boolean;
        message?: string;
        exists?: boolean;
        status?: string;
        sslStatus?: string;
        error?: string;
      }>('/portals/check-tls', {
        customDomain: portal.customDomain,
        portalId: portal.id,
      });

      if (result.success) {
        this.msgSrvc.show('✅ TLS certificate is valid and ready! Your portal can be published securely.');
      } else {
        let message = '❌ TLS certificate is not ready.';
        let isError = true;

        if (!result.exists) {
          message =
            '❌ TLS certificate has not been created yet. This usually takes a few minutes after DNS is configured.';
        } else if (result.error && result.error.includes('CNAME Configuration Error')) {
          // Show specific CNAME error message
          message = result.error;
        } else if (result.status !== 'active' || result.sslStatus !== 'active') {
          message = `🔄 TLS certificate is still being provisioned. Current status: Hostname ${result.status}, SSL ${result.sslStatus}.`;
          isError = false; // This is not an error, just in progress
        }

        if (isError) {
          this.msgSrvc.show(message, {
            duration: 0,
            buttons: [
              {
                text: '✕',
                role: 'cancel',
                handler: () => {
                  // Toast will be dismissed automatically
                },
              },
            ],
          });
        } else {
          this.msgSrvc.show(message); // Auto-dismiss for in-progress messages
        }
      }
    } catch (error: any) {
      if (error.status === 403) {
        this.msgSrvc.show('⚠️ Access denied. Please try refreshing the page and try again.');
      } else {
        this.msgSrvc.show('⚠️ TLS certificate check failed. Please try again later.');
      }
    } finally {
      this.isTlsTesting = false;
      this.currentTestingPortal = null;
      this.currentTlsStep = 0;
    }
  }

  // Generic copy to clipboard method
  async copyToClipboard(text: string, label?: string) {
    try {
      await navigator.clipboard.writeText(text);
      this.msgSrvc.show(label ? `${label} copied to clipboard` : 'Copied to clipboard');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      this.msgSrvc.show(label ? `${label} copied to clipboard` : 'Copied to clipboard');
    }
  }

  async deletePortal(id: string) {
    const alert = await this.alertCtrl.create({
      header: 'Delete Portal',
      message: 'Are you sure you want to delete this portal? This action cannot be undone.',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Delete',
          role: 'destructive',
          handler: async () => {
            this.isLoading = true;
            try {
              await this.portalsService.delete(id);

              // Remove the portal from the local array
              this.portals = this.portals.filter((p) => p.id !== id);
              this.hasPortal = this.portals.length > 0;

              this.msgSrvc.show('Portal deleted successfully');
            } catch (err) {
              this.msgSrvc.showError(err);
            } finally {
              this.isLoading = false;
            }
          },
        },
      ],
    });

    await alert.present();
  }

  async publishPortal(id: string) {
    const portal = this.portals.find((p) => p.id === id);
    if (!portal) return;
    const customDomain = portal.customDomain || '';

    this.isLoading = true;

    // Step 1: Test DNS configuration
    let dnsResult: { success: boolean; message?: string } | undefined;
    try {
      dnsResult = await this.apiService.postPromise<{
        success: boolean;
        message?: string;
        reason?: string;
        cnameRecords?: string[];
        expectedTarget?: string;
        error?: string;
      }>('/portals/test-dns', {
        customDomain: portal.customDomain,
        portalId: portal.id,
      });
    } catch (error: any) {
      this.isLoading = false;
      await this.alertCtrl
        .create({
          header: 'DNS Test Failed',
          message: 'Could not verify DNS configuration. Please try again or check your DNS settings.',
          buttons: ['OK'],
        })
        .then((alert) => alert.present());
      return;
    }

    if (!dnsResult?.success) {
      this.isLoading = false;
      await this.alertCtrl
        .create({
          header: 'Cannot Publish Portal',
          message: `
          <div style="text-align: left;">
            <strong>You cannot publish this portal until your DNS is properly configured.</strong><br><br>
            ${dnsResult?.message || `❌ CNAME Configuration Error: Your domain ${customDomain} does not have a CNAME record pointing to portal.unthread.io. Please add a CNAME record in your DNS settings.`}<br><br>
            <ul style="margin-top: 8px; margin-bottom: 8px;">
              <li>Make sure your subdomain <code>${customDomain}</code> points to <code>portal.unthread.io</code>.</li>
              <li>DNS changes may take up to 24 hours to propagate.</li>
              <li>After updating your DNS, try publishing again.</li>
            </ul>
          </div>
        `,
          buttons: ['OK'],
        })
        .then((alert) => alert.present());
      return;
    }

    // Step 2: Check TLS certificate status
    let tlsResult:
      | { success: boolean; message?: string; exists?: boolean; status?: string; sslStatus?: string }
      | undefined;
    try {
      tlsResult = await this.apiService.postPromise<{
        success: boolean;
        message?: string;
        exists?: boolean;
        status?: string;
        sslStatus?: string;
        error?: string;
      }>('/portals/check-tls', {
        customDomain: portal.customDomain,
        portalId: portal.id,
      });
    } catch (error: any) {
      this.isLoading = false;
      await this.alertCtrl
        .create({
          header: 'TLS Certificate Check Failed',
          message: 'Could not verify TLS certificate status. Please try again.',
          buttons: ['OK'],
        })
        .then((alert) => alert.present());
      return;
    }

    if (!tlsResult?.success) {
      this.isLoading = false;
      let tlsMessage = tlsResult?.message || 'TLS certificate is not ready';

      if (!tlsResult?.exists) {
        tlsMessage =
          'TLS certificate has not been created yet. This usually takes a few minutes after DNS is configured.';
      } else if (tlsResult?.message && tlsResult.message.includes('CNAME Configuration Error')) {
        // Show specific CNAME error message
        tlsMessage = tlsResult.message;
      } else if (tlsResult?.status !== 'active' || tlsResult?.sslStatus !== 'active') {
        tlsMessage = `TLS certificate is still being provisioned. Current status: Hostname ${tlsResult?.status}, SSL ${tlsResult?.sslStatus}. This can take up to 10 minutes.`;
      }

      await this.alertCtrl
        .create({
          header: 'Cannot Publish Portal',
          message: `
          <div style="text-align: left;">
            <strong>TLS certificate not ready.</strong><br><br>
            ${tlsMessage}<br><br>
            <div style="margin-top: 12px; padding: 8px; background: #f8f9fa; border-radius: 6px; border-left: 3px solid #007bff; font-size: 14px;">
              <strong>Note:</strong> Unthread handles TLS certificates for you. If the certificate is not ready in 10 minutes, contact <a href="mailto:jake@unthread.io" style="color: #007bff;">jake@unthread.io</a> for assistance.
            </div>
          </div>
        `,
          buttons: ['OK'],
        })
        .then((alert) => alert.present());
      return;
    }

    this.isLoading = false;

    // Use alert for publish confirmation with similar styling
    const alert = await this.alertCtrl.create({
      header: '',
      message: `
        <div style="
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 40px 32px 32px 32px;
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 4px 24px rgba(0,0,0,0.10);
          max-width: 440px;
          margin: 0 auto;
          text-align: left;
          min-height: 340px;
        ">
          <div style="
            display: flex;
            align-items: center;
            margin-bottom: 28px;
            width: 100%;
            justify-content: center;
          ">
            <span style="
              font-size: 32px;
              margin-right: 12px;
              color: #16a34a;
              display: flex;
              align-items: center;
            ">✅</span>
            <span style="
              font-weight: 600;
              font-size: 18px;
              color: #16a34a;
              display: flex;
              align-items: center;
            ">DNS is properly configured. You are ready to publish!</span>
          </div>
          <div style="
            margin-bottom: 32px;
            width: 100%;
            text-align: center;
          ">
            <div style="
              font-size: 16px;
              font-weight: 600;
              margin-bottom: 18px;
            ">Are you sure you want to publish this portal?</div>
            <div style="
              font-size: 15px;
              color: #222;
              margin-bottom: 0;
            ">
              Once you do so, anyone who visits
              <span style="
                display: inline-block;
                background: #f4f4f5;
                border-radius: 4px;
                padding: 2px 8px;
                font-family: 'Courier New', monospace;
                font-size: 14px;
                color: #222;
                margin: 0 2px;
              ">${customDomain}</span>
              will be able to access relevant knowledge bases and resources.
            </div>
          </div>
        </div>
      `,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          cssClass: 'cancel-btn',
          handler: () => {
            // Do nothing, just dismiss
          },
        },
        {
          text: 'Publish',
          role: 'confirm',
          cssClass: 'publish-btn',
          handler: async () => {
            this.isLoading = true;
            try {
              await this.portalsService.update(id, { isEnabled: true });
              // Update the portal in the local array
              const portalIndex = this.portals.findIndex((p) => p.id === id);
              if (portalIndex !== -1) {
                this.portals[portalIndex].isEnabled = true;
              }
              this.msgSrvc.show('Portal published successfully');
            } catch (err) {
              this.msgSrvc.showError(err);
            } finally {
              this.isLoading = false;
            }
          },
        },
      ],
      cssClass: 'publish-portal-alert',
    });

    await alert.present();
  }

  async disablePortal(id: string) {
    const alert = await this.alertCtrl.create({
      header: 'Disable Portal',
      message: 'Are you sure you want to disable this portal? Users will no longer be able to access it.',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Disable',
          role: 'destructive',
          handler: async () => {
            this.isLoading = true;
            try {
              await this.portalsService.update(id, { isEnabled: false });

              // Update the portal in the local array
              const portalIndex = this.portals.findIndex((p) => p.id === id);
              if (portalIndex !== -1) {
                this.portals[portalIndex].isEnabled = false;
              }

              this.msgSrvc.show('Portal disabled successfully');
            } catch (err) {
              this.msgSrvc.showError(err);
            } finally {
              this.isLoading = false;
            }
          },
        },
      ],
    });

    await alert.present();
  }

  async showLogoModal(logoUrl: string) {
    const alert = await this.alertCtrl.create({
      header: 'Portal Logo',
      message: `<div style="text-align: center; padding: 20px;">
        <img src="${logoUrl}" style="max-width: 100%; max-height: 60vh; width: auto; height: auto; object-fit: contain; border-radius: 8px;" />
      </div>`,
      buttons: ['Close'],
      cssClass: 'logo-modal',
    });

    await alert.present();
  }

  // Domain validation method
  validateDomain(domain: string): string {
    if (!domain) {
      return '';
    }

    // Remove protocol if present
    const cleanDomain = domain.replace(/^https?:\/\//, '');

    // Basic domain regex pattern
    const domainPattern =
      /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

    if (!domainPattern.test(cleanDomain)) {
      const exampleDomain = this.tenant?.subdomain ? `support.${this.tenant.subdomain}.com` : 'support.yourcompany.com';
      return `Please enter a valid domain (e.g., ${exampleDomain})`;
    }

    // Check for common invalid domains
    const invalidDomains = ['localhost', '127.0.0.1', '0.0.0.0'];
    if (invalidDomains.includes(cleanDomain.toLowerCase())) {
      return 'Please enter a valid domain name';
    }

    return '';
  }

  // Handle project checkbox changes
  onProjectCheckboxChange(projectId: string, event: CustomEvent<{ checked: boolean }>) {
    if (event.detail.checked) {
      if (!this.portalForm.selectedProjectIds.includes(projectId)) {
        this.portalForm.selectedProjectIds.push(projectId);
      }
    } else {
      this.portalForm.selectedProjectIds = this.portalForm.selectedProjectIds.filter((id) => id !== projectId);
    }
  }
}
